import { useState, useEffect } from 'react';
import { apiClient } from '../api/client';

const SESSION_KEY = 'kdu_chapter_prompt_dismissed';

interface ChapterOption {
  id: string;
  name: string;
}

interface UseChapterPromptReturn {
  shouldPrompt: boolean;
  chapters: ChapterOption[];
  loading: boolean;
  error: string | null;
  dismiss: () => void;
  submit: (action: 'select' | 'none' | 'suppress', chapterName?: string) => Promise<void>;
}

export function useChapterPrompt(): UseChapterPromptReturn {
  const [shouldPrompt, setShouldPrompt] = useState(false);
  const [chapters, setChapters] = useState<ChapterOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check sessionStorage first; if dismissed this session, skip API call
    if (sessionStorage.getItem(SESSION_KEY) === 'true') {
      setLoading(false);
      return;
    }

    apiClient<{ shouldPrompt: boolean; chapters: ChapterOption[] }>(
      '/chapter-affiliation/prompt-status',
    )
      .then((data) => {
        setShouldPrompt(data.shouldPrompt);
        setChapters(data.chapters);
      })
      .catch(() => {
        // Non-blocking: if the check fails, just don't show the popup
      })
      .finally(() => setLoading(false));
  }, []);

  function dismiss() {
    sessionStorage.setItem(SESSION_KEY, 'true');
    setShouldPrompt(false);
  }

  async function submit(
    action: 'select' | 'none' | 'suppress',
    chapterName?: string,
  ) {
    setError(null);
    try {
      await apiClient('/chapter-affiliation', {
        method: 'PATCH',
        body: { action, chapterName },
      });
      setShouldPrompt(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save chapter selection');
      throw err;
    }
  }

  return { shouldPrompt, chapters, loading, error, dismiss, submit };
}
